import { Note, Chord } from 'tonal';

/**
 * Transpose a single chord name by semitones, with key-aware enharmonic spelling.
 * @param {string} chordName  e.g. "G", "Dm7", "F#maj7"
 * @param {number} semitones  positive = up, negative = down
 * @param {string} [preferSharps] 'sharps' | 'flats' | null (auto)
 */
export function transposeChord(chordName, semitones, preferSharps = null) {
  if (!chordName) return chordName;

  // Handle slash chords: transpose both root and bass independently
  const slashIdx = chordName.lastIndexOf('/');
  if (slashIdx > 0) {
    const root = chordName.slice(0, slashIdx);
    const bass = chordName.slice(slashIdx + 1);
    return transposeChord(root, semitones, preferSharps) + '/' + transposeNote(bass, semitones, preferSharps);
  }

  const parsed = Chord.get(chordName);
  if (!parsed.tonic) return chordName;

  const newTonic = transposeNote(parsed.tonic, semitones, preferSharps);

  // Preserve quality suffix exactly as written in the original name (not tonal's alias)
  const quality = chordName.slice(parsed.tonic.length);

  return newTonic + quality;
}

function transposeNote(noteName, semitones, preferSharps) {
  const midi = Note.midi(noteName + '4');
  if (midi === null) return noteName;
  const newNote = Note.pitchClass(Note.fromMidi(midi + semitones));
  if (preferSharps === 'flats') return sharpToFlat(newNote);
  if (preferSharps === 'sharps') return flatToSharp(newNote);
  return newNote;
}

function sharpToFlat(note) {
  // Always convert A#→Bb and D#→Eb (universal guitar convention)
  const map = { 'C#': 'Db', 'D#': 'Eb', 'F#': 'Gb', 'G#': 'Ab', 'A#': 'Bb' };
  return map[note] || note;
}

function flatToSharp(note) {
  // Convert flat→sharp for sharp keys, but NEVER convert Bb→A# or Eb→D#:
  // guitarists universally write Bb and Eb, even in sharp keys.
  const map = { 'Db': 'C#', 'Gb': 'F#', 'Ab': 'G#' };
  return map[note] || note;
}

/**
 * Transpose all chords in an alignment array.
 * @param {Array<{chord: string, lineIndex: number, wordIndex: number}>} alignment
 * @param {number} semitones
 * @param {string} [preferSharps]
 */
export function transposeAlignment(alignment, semitones, preferSharps = null) {
  return alignment.map(item => ({
    ...item,
    chord: transposeChord(item.chord, semitones, preferSharps),
  }));
}

/**
 * Given a capo fret and desired sounding chord, return the chord shape to play.
 * e.g. capo=2, sounding="A" → play "G"
 * @param {string} soundingChord
 * @param {number} capoFret
 */
export function chordShapeForCapo(soundingChord, capoFret) {
  if (!capoFret || capoFret === 0) return soundingChord;
  // Transpose down by capo fret; let the key preference follow the sounding chord spelling
  const parsed = Chord.get(soundingChord.split('/')[0]);
  const tonic = parsed.tonic || soundingChord[0];
  const flatKeys = ['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb'];
  const pref = flatKeys.includes(tonic) ? 'flats' : 'sharps';
  return transposeChord(soundingChord, -capoFret, pref);
}

/**
 * Conservatively simplify chord color while preserving harmonic function.
 *
 * Design goal:
 * - Keep core quality changes that materially affect harmony (minor/major/dim/aug/sus, 7th class).
 * - Remove optional color tones (add9/9/11/13 variants).
 * - Preserve slash bass because bass inversions can be harmonically significant.
 *
 * Examples:
 *   Gm9      -> Gm7
 *   C13      -> C7
 *   Fmaj9    -> Fmaj7
 *   Dsus2add9-> Dsus2
 *   Bb/F     -> Bb/F
 */
export function simplifyChord(chordName) {
  if (!chordName) return chordName;
  const [head, slashBass] = chordName.split('/');
  const parsed = Chord.get(head);
  const tonic = parsed?.tonic || (head.match(/^([A-G](?:#|b)?)/)?.[1] || null);
  if (!tonic) return chordName;
  const suffixRaw = head.slice(tonic.length);
  const s = suffixRaw.toLowerCase();

  let core = '';
  // Preserve suspended quality before extension normalization.
  if (/sus2/.test(s)) core = 'sus2';
  else if (/sus4|sus/.test(s)) core = 'sus4';
  // Preserve 7th "function class" first.
  else if (/m7b5|min7b5|ø/i.test(suffixRaw)) core = 'm7b5';
  else if (/^(?:m|min|mi|-).*(?:maj|ma|M|Δ|\^)/.test(suffixRaw)) core = 'mMaj7';
  else if (/(^|[^A-Za-z])m(?!aj)/.test(suffixRaw) && /(?:7|9|11|13)/.test(suffixRaw)) core = 'm7';
  else if (/^(?:min|mi|-).*(?:7|9|11|13)/i.test(suffixRaw)) core = 'm7';
  else if (/maj(?:9|11|13)/i.test(suffixRaw)) core = 'maj7';
  else if (/(?:maj7|Maj7|ma7|M7|Δ7|\^7)/.test(suffixRaw)) core = 'maj7';
  else if (/(?:7|9|11|13)/.test(suffixRaw)) core = '7';
  // Otherwise preserve basic quality.
  else if (/dim|o/.test(s)) core = 'dim';
  else if (/aug|\+/.test(s)) core = 'aug';
  else if (/(^|[^A-Za-z])m(?!aj)/.test(suffixRaw) || /min/.test(s)) core = 'm';
  else core = '';

  let out = tonic + core;
  if (slashBass) out += `/${slashBass}`;
  return out;
}


export function capoLabel(capoFret) {
  if (!capoFret || capoFret === 0) return 'No Capo';
  return `Capo ${capoFret}`;
}

/**
 * Determine preferred accidental style from a key string like "G major" or "Bb major"
 * @param {string} key e.g. "G major", "F major"
 * @returns 'sharps' | 'flats'
 */
export function accidentalPreference(key) {
  if (!key) return 'sharps';
  const flatKeys = ['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb'];
  const tonic = key.split(' ')[0];
  return flatKeys.includes(tonic) ? 'flats' : 'sharps';
}

/**
 * Suggest capo positions that bring the song into a guitar-friendly open-position key.
 *
 * @param {string} songKey   e.g. "Bb major", "F# minor"
 * @returns {Array<{capo, playKey, display}>} sorted by capo fret asc
 */
export function suggestCapo(songKey) {
  if (!songKey) return [];

  // Guitar-friendly open keys: easy barre or open shapes
  const FRIENDLY = new Set(['C', 'G', 'D', 'A', 'E', 'Am', 'Em', 'Dm', 'Bm']);

  const [tonic, mode] = songKey.trim().split(/\s+/);
  if (!tonic) return [];

  const suffix = mode === 'minor' ? 'm' : '';
  const suggestions = [];

  for (let capoFret = 0; capoFret <= 7; capoFret++) {
    // Placing capo at `capoFret` means the sounding pitch is fret higher,
    // so the open shape to play is transposed DOWN by capoFret
    const playKey = transposeChord(tonic + suffix, -capoFret, 'sharps');
    const playKeyFlat = transposeChord(tonic + suffix, -capoFret, 'flats');

    const displayKey = FRIENDLY.has(playKey) ? playKey : (FRIENDLY.has(playKeyFlat) ? playKeyFlat : null);
    if (displayKey) {
      suggestions.push({
        capo: capoFret,
        playKey: displayKey,
        display: capoFret === 0
          ? `No capo — play in ${displayKey} shapes`
          : `Capo ${capoFret} — play ${displayKey} shapes`,
      });
    }
  }

  return suggestions;
}
